import {cubejsApi, getDimensions} from "@/cube/cubejsApi"
import {calculateChangeRatePercent} from "@/ui/chart/calculateChangeRatePercent"
import {
  Dimension,
  MemberSnapshotCube,
  MemberSnapshotDimension,
  Metric,
} from "@/cube"
import {toCubeFormat} from "@/cube/toCubeFormat"
import Vue from "vue"
import {DateRange} from "@/DateRangeSelector"
import {toKey} from "@/cube/toKey"
import {toQueryOrderBy} from "@/cube/toQueryOrderBy"
import * as _ from "lodash"

type ResultSetRow<M extends Metric> = {
  [MemberSnapshotCube._atMonth]: string
} & {
  [key in M]: number
} &
  {
    [P in Dimension]?: any
  }

// Charts consume series as arrays with a flat model, just like the cube query
// results... and allow to re-use the cube data directly (only works when the
// filter is applied in the backend).
export interface MetricSliced<M extends Metric> {
  readonly metric: M

  dateRange?: DateRange
  end?: ReadonlyArray<ResultSetRow<M>>

  changeRateByKey: {
    [key: string]:
      | {
          readonly changeRate?: number
          readonly percentChangeRate: number
        }
      | undefined
  }
}

export interface MetricSnapshot {
  readonly amount: number
  readonly amountPercent: number
  readonly totalAmount: number
}

export interface MetricAggregated {
  readonly metric: Metric
  readonly title: string

  readonly dateRange?: DateRange
  readonly end?: MetricSnapshot
  readonly start?: MetricSnapshot

  readonly changeRate?: number
  readonly percentChangeRate?: number

  readonly amountFilter: {[key in Dimension]?: unknown}

  readonly accumulatedAmount?: number
}

export interface MetricHistory extends MetricAggregated {
  readonly timeSeries: any
}

export interface QueryFilter {
  dimension: string
  operator: string
  values: Array<string>
}

class MetricAggregatedImpl implements MetricAggregated {
  _title: string = <any>null

  _dateRange: DateRange = <any>null
  _start?: MetricSnapshot = <any>null
  _end?: MetricSnapshot = <any>null

  amountFilter: {[key in Dimension]?: unknown}

  _accumulatedAmount: number = <any>null

  constructor(
    readonly metric: Metric,
    amountFilter: {[key in Dimension]?: unknown}
  ) {
    this.amountFilter = Object.freeze(amountFilter)
  }

  get title(): string {
    return this._title
  }

  get dateRange(): DateRange {
    return this._dateRange
  }

  get end(): MetricSnapshot | undefined {
    return this._end
  }

  get start(): MetricSnapshot | undefined {
    return this._start
  }

  get changeRate(): number | undefined {
    return this._end == null
      ? undefined
      : calculateChangeRatePercent(this._end.amount, this._start!.amount)
  }

  get percentChangeRate(): number | undefined {
    return this._end && this._start
      ? _.round((this._end.amountPercent - this._start.amountPercent) * 100, 2)
      : undefined
  }

  get accumulatedAmount(): number | undefined {
    return this._accumulatedAmount
  }
}

export type Filter = {[key in Dimension]?: Array<string>}

export class MetricStore {
  private readonly baseQuery = <const>{
    timeDimensions: [
      {
        dimension: MemberSnapshotCube._at,
        granularity: "month",
      },
    ],
  }

  private readonly metricAggregatedMap = new Map<string, MetricAggregatedImpl>()
  private readonly metricSlicedMap = new Map<string, MetricSliced<any>>()

  private planId: string | null = null
  private dateRange: DateRange
  private filter: Filter = {}
  private engagedOnly: boolean
  private updating = false

  constructor(engagedOnly = false) {
    this.engagedOnly = engagedOnly
  }

  get(
    metric: Metric,
    amountFilter: {[K in Dimension]?: unknown} = {}
  ): MetricAggregated {
    // FIXME Need to create key using value as well
    const dimensions = <Array<Dimension | string>>(
      (<[string, unknown]>Object.entries(amountFilter).sort())
    )

    const key = `${metric}|${dimensions.toString().replace(",", "|")}`
    console.log("Looking up metric by key", key, this.metricAggregatedMap)
    let metricAggregated = this.metricAggregatedMap.get(key)

    if (metricAggregated == null) {
      metricAggregated = Vue.observable(
        new MetricAggregatedImpl(metric, amountFilter)
      )

      console.log("creating new metric:", metric, metricAggregated)
      this.metricAggregatedMap.set(key, metricAggregated)
    }

    return metricAggregated
  }

  getHistory(
    metric: Metric,
    amountFilter: {[K in Dimension]?: unknown} = {}
  ): MetricHistory {
    const metricAggregated = <any>this.get(metric, amountFilter)

    if (metricAggregated.timeSeries === undefined) {
      metricAggregated.timeSeries = null
    }

    return metricAggregated
  }

  getSliced<M extends Metric>(
    metric: M,
    dimensions: Array<Dimension>
  ): MetricSliced<M> {
    const key = `${metric}|${dimensions.toString()}`
    console.log("Looking up MetricSliced by key", key, this.metricSlicedMap)
    let metricSliced = this.metricSlicedMap.get(key)

    if (metricSliced == null) {
      metricSliced = Vue.observable({
        metric,
        end: <any>null,
        dateRange: <any>null,
        changeRateByKey: {},
      })

      console.log("creating new metric:", metric, metricSliced)
      this.metricSlicedMap.set(key, metricSliced)
    }

    return metricSliced
  }

  async setFilter(filter: Filter): Promise<void> {
    this.filter = filter
    await this.update(this.planId!, this.dateRange)
  }

  async setEngagedOnly(engagedOnly: boolean): Promise<void> {
    this.engagedOnly = engagedOnly
    if (this.planId != null) {
      await this.update(this.planId, this.dateRange)
    }
  }

  isUpdating(): boolean {
    return this.updating
  }

  async update(planId: string, dateRange: DateRange): Promise<void> {
    this.updating = true
    this.planId = planId
    this.dateRange = dateRange

    const waitFor = Array<Promise<void>>()
    this.metricSlicedMap.forEach((sliced, key) => {
      waitFor.push(this.updateSliced(sliced, key))
    })

    this.metricAggregatedMap.forEach(aggregated => {
      waitFor.push(this.updateAggregated(aggregated))
    })

    await Promise.all(waitFor).finally(() => (this.updating = false))
  }

  private getQueryFilter(): Array<QueryFilter> {
    return [
      {
        dimension: "Member.planId",
        operator: "equals",
        values: [this.planId!],
      },
    ]
      .concat(
        Object.entries(this.filter).map(([filterKey, entries]) => ({
          dimension: filterKey,
          operator: "equals",
          values: entries,
        }))
      )
      .concat(
        this.engagedOnly
          ? [
              {
                dimension: "Member.engaged",
                operator: "equals",
                values: ["1"],
              },
            ]
          : []
      )
  }

  private async updateSliced(metricSliced: MetricSliced<Metric>, key: string) {
    metricSliced.dateRange = this.dateRange
    console.log("Updating metricSliced", metricSliced, this)
    const dimensions = <Array<Dimension>>key.split("|")[1].split(",")
    console.log("Dimensions from key", dimensions)

    const queryResult = await this.runQuery(
      metricSliced.metric,
      dimensions,
      this.getQueryFilter()
    )

    const dataByDate = _.groupBy(
      queryResult.data,
      MemberSnapshotDimension._atMonth
    )

    const endData =
      <Array<any> | undefined>(
        dataByDate[_.last(queryResult.data)?.[MemberSnapshotDimension._atMonth]]
      ) ?? []

    const startData =
      <Array<any> | undefined>dataByDate[toCubeFormat(this.dateRange.start)] ??
      []

    const startDataByKey = startData.reduce<any>((acc, dataItem) => {
      acc[toKey(dataItem, dimensions)] = dataItem[metricSliced.metric]
      return acc
    }, {})

    const changeRateByKey = endData.reduce<any>((acc, row) => {
      const changeRateKey = toKey(row, dimensions)
      const startValue = startDataByKey[changeRateKey]

      const changeRate =
        startValue == null ? Infinity : startValue - row[metricSliced.metric]

      acc[changeRateKey] = {
        changeRate,
        // FIXME calculate it
        percentChangeRate: 0,
      }

      return acc
    }, {})

    metricSliced.end = Object.freeze(endData)
    metricSliced.changeRateByKey = Object.freeze(changeRateByKey)
  }

  private async updateAggregated(metricAggregated: MetricAggregatedImpl) {
    // TODO Support client side filter for countable values?
    const amountFilterDimensions = <Array<Dimension>>(
      Object.keys(metricAggregated.amountFilter!)
    )

    const queryResult = await this.runQuery(
      metricAggregated.metric,
      amountFilterDimensions,
      this.getQueryFilter()
    )

    const filteredDataSet = queryResult.data

    const dimensionName = <string | undefined>amountFilterDimensions[0]
    metricAggregated._title =
      dimensionName === undefined
        ? queryResult.annotation.measures[metricAggregated.metric].shortTitle
        : await getDimensions().then(
            dimensionMetaData =>
              dimensionMetaData.find(data => data.name === dimensionName)
                .shortTitle
          )

    const dataByDate = _.groupBy(
      filteredDataSet,
      MemberSnapshotDimension._atMonth
    )

    const endData = dataByDate[toCubeFormat(this.dateRange.end)]

    metricAggregated._dateRange = this.dateRange
    metricAggregated._end = this.toMetricSnapshot(endData, metricAggregated)

    const startData = dataByDate[toCubeFormat(this.dateRange.start)]

    metricAggregated._start = this.toMetricSnapshot(startData, metricAggregated)

    const asMetricHistory = <any>metricAggregated
    if (asMetricHistory.timeSeries !== undefined) {
      const dataSet = _.isEmpty(metricAggregated.amountFilter)
        ? filteredDataSet
        : _.filter(filteredDataSet, metricAggregated.amountFilter)
      asMetricHistory.timeSeries = Object.freeze(dataSet)
      // Accumulate all metric values over past year
      metricAggregated._accumulatedAmount = 0
      asMetricHistory.timeSeries
        .filter(
          (dataAt: {[x: string]: string}) =>
            new Date(dataAt[MemberSnapshotCube._at]) >=
            new Date(new Date().setFullYear(new Date().getFullYear() - 1))
        )
        .forEach((dataAt: {[x: string]: number}) => {
          metricAggregated._accumulatedAmount += dataAt[metricAggregated.metric]
        })
    }
  }

  private toMetricSnapshot(
    dataAt: any,
    metricAggregated: MetricAggregatedImpl
  ): MetricSnapshot {
    const amount = _.sumBy(
      _.filter(dataAt, metricAggregated.amountFilter),
      metricAggregated.metric
    )

    const totalAmount = _.sumBy(dataAt, metricAggregated.metric)
    console.log(
      "MetricSnapshot",
      metricAggregated.metric,
      amount,
      totalAmount,
      dataAt
    )
    const amountPercent = _.round(amount / totalAmount, 3)

    return {amount, totalAmount, amountPercent}
  }

  private async runQuery(
    metric: Metric,
    dimensions: Array<Dimension>,
    filters: Array<QueryFilter>
  ) {
    const measures = [metric]

    const query = {
      ...this.baseQuery,
      measures,
      dimensions,
      filters,
      order: toQueryOrderBy([MemberSnapshotDimension._at, ...dimensions]),
    }

    console.log("Running query", query)
    const queryResult = (await cubejsApi.load(query)).loadResponse.results[0]
    console.log("Query result", queryResult)

    return queryResult
  }
}
